import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { Button, Modal, Select } from 'slab'
import { usePostAdminEmployerOpenEnrollmentIdCohort } from 'utils/web_api'
import { type AdminEmployerOpenEnrollment, type PostAdminEmployerOpenEnrollmentIdCohortCohort } from 'utils/web_api.schemas'

const COHORT_LABELS: Record<string, string> = {
  renewal_self_serve: 'Renewal Self-Serve',
  renewal_high_touch: 'Renewal High Touch',
  renewal_custom: 'Renewal Custom',
  renewal_sales: 'Renewal Sales',
  new_employer_self_serve: 'New Employer Self-Serve',
  new_employer_sales: 'New Employer Sales',
}

const ALL_COHORTS = [
  'renewal_self_serve',
  'renewal_high_touch',
  'renewal_custom',
  'renewal_sales',
  'new_employer_self_serve',
  'new_employer_sales',
]

interface CohortSelectProps {
  employer: AdminEmployerOpenEnrollment
}

const CohortSelect: React.FC<CohortSelectProps> = ({ employer }) => {
  const [selectedCohort, setSelectedCohort] = useState(employer.open_enrollment_config?.cohort || '')
  const [pendingCohort, setPendingCohort] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const mutation = usePostAdminEmployerOpenEnrollmentIdCohort()
  const queryClient = useQueryClient()

  const handleCohortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!employer.open_enrollment_config?.id) return

    const newCohort = e.target.value as PostAdminEmployerOpenEnrollmentIdCohortCohort

    // Show confirmation modal
    setPendingCohort(newCohort)
    setIsModalOpen(true)
  }

  const handleConfirm = async () => {
    if (!employer.open_enrollment_config?.id || !pendingCohort) return

    setSelectedCohort(pendingCohort)

    try {
      await mutation.mutateAsync({
        id: employer.open_enrollment_config.id,
        data: { cohort: pendingCohort as PostAdminEmployerOpenEnrollmentIdCohortCohort }
      })

      toast.success('Successfully updated cohort')
      // Invalidate queries to refresh the table data
      queryClient.invalidateQueries({ queryKey: ['getAdminEmployerOpenEnrollment'] })
      setIsModalOpen(false)
      setPendingCohort(null)
    } catch (error) {
      setSelectedCohort(employer.open_enrollment_config?.cohort || '')
      toast.error('Failed to update cohort')
      console.error('Error updating cohort:', error)
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setPendingCohort(null)
    // Revert the select to the original value
    setSelectedCohort(employer.open_enrollment_config?.cohort || '')
  }

  if (!employer.open_enrollment_config) return <span>—</span>

  const originalCohort = employer.open_enrollment_config.cohort
  const originalCohortLabel = originalCohort ? COHORT_LABELS[originalCohort] || originalCohort : ''
  const pendingCohortLabel = pendingCohort ? COHORT_LABELS[pendingCohort] || pendingCohort : ''

  return (
    <>
      <Select
        value={selectedCohort}
        onChange={handleCohortChange}
        disabled={mutation.isPending}
      >
        {ALL_COHORTS.map(cohort => (
          <option key={cohort} value={cohort}>
            {COHORT_LABELS[cohort] || cohort}
          </option>
        ))}
      </Select>

      <Modal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        width='medium'
      >
        <Modal.Header>
          <Modal.Title dismissible>
            <div className='flex items-center gap-2'>
              <AlertTriangle className='h-5 w-5 text-yellow-600' />
              Confirm Cohort Change
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Modal.Description>
            Are you sure you want to change the cohort from <strong className='whitespace-nowrap'>{originalCohortLabel}</strong> to <strong className='whitespace-nowrap'>{pendingCohortLabel}</strong>?
            <hr className='my-6' />
            <ul className='list-disc list-inside'>
              <li><strong>Renewal</strong> cohorts are used for employers that have current year coverage and are renewing their coverage for the next year.</li>
              <li><strong>New Employer</strong> cohorts are used for employers that are new to Thatch starting next year.</li>
              <li><strong>Self-serve</strong> cohorts allow employers to self-serve their new or renewing coverage.</li>
            </ul>
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-between'>
            <Button
              variant='secondary'
              onClick={handleCancel}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleConfirm}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Updating...
                  </>
                  )
                : (
                    'Confirm Change'
                  )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CohortSelect
